import React, {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from 'react';
import { getBrowserCountry } from '../Common/Utils/locations';
import {
  getValue,
  fetchAndActivate,
  getRemoteConfig,
  ensureInitialized,
} from 'firebase/remote-config';
import app from '../Firebase';
import env from '../env';

interface IFeaturesState {
  wallet: boolean;
  autoActivation: boolean;
  offerwall: boolean;
  loading: boolean;
}

const defaultState: IFeaturesState = {
  wallet: false,
  autoActivation: false,
  offerwall: false,
  loading: true,
};

const FeaturesContext = createContext(defaultState);

export const useFeatures = () => useContext(FeaturesContext);

interface IFeatureFlagToggle {
  active: boolean;
  countries?: string[];
}

interface IFeatureFlag {
  wallet: IFeatureFlagToggle;
  offerwall: IFeatureFlagToggle;
  autoActivation: IFeatureFlagToggle;
  [key: string]: IFeatureFlagToggle;
}

type IPartnerFeatureFlags = Record<string | 'default', IFeatureFlag>;

const defaultPartnerFeatureFlags: IPartnerFeatureFlags = {
  default: {
    wallet: {
      active: true,
    },
    offerwall: {
      active: false,
    },
    autoActivation: {
      active: true,
    },
  },
};

export const FeaturesProvider: React.FC<PropsWithChildren<unknown>> = ({
  children,
}) => {
  const [loading, setLoading] = useState(true);
  const [autoActivation, setAutoActivation] = useState(false);
  const [offerwall, setOfferwall] = useState(false);
  const [wallet, setWallet] = useState(false);

  useEffect(() => {
    const setFeatures = async (features: IFeatureFlag) => {
      const countryCode = getBrowserCountry() ?? '';
      const keys = ['wallet', 'autoActivation', 'offerwall'];
      const setter: Record<
        string,
        React.Dispatch<React.SetStateAction<boolean>>
      > = {
        wallet: setWallet,
        autoActivation: setAutoActivation,
        offerwall: setOfferwall,
      };
      for (const key of keys) {
        const feature: IFeatureFlagToggle = features[key];
        if (
          feature &&
          (!feature.countries ||
            feature.countries.length === 0 ||
            feature.countries.includes(countryCode))
        ) {
          setter[key](feature.active);
        } else {
          setter[key](false);
        }
      }
    };

    const fetchRemoteConfig = async () => {
      const remoteConfig = await getRemoteConfig(app);
      remoteConfig.settings.minimumFetchIntervalMillis = 0;
      remoteConfig.defaultConfig = JSON.parse(
        JSON.stringify(defaultPartnerFeatureFlags)
      );

      await ensureInitialized(remoteConfig);
      await fetchAndActivate(remoteConfig);

      setLoading(true);
      const partnerFeatureFlagsStr = await getValue(
        remoteConfig,
        env.partner_features_remote_config
      );

      if (partnerFeatureFlagsStr) {
        const partnerFeatureFlags = JSON.parse(
          partnerFeatureFlagsStr.asString()
        );

        if (partnerFeatureFlags as IPartnerFeatureFlags) {
          const features = partnerFeatureFlags['default'];
          setFeatures(features);
        }
      }

      setLoading(false);
    };

    fetchRemoteConfig();
  }, []);

  return (
    <FeaturesContext.Provider
      value={{ loading, autoActivation, offerwall, wallet }}
    >
      {children}
    </FeaturesContext.Provider>
  );
};
